import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, ArrowLeft, Shield, Building2, Phone, User, Loader } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

type Step = 1 | 2 | 3 | 4 | 5;

interface FormData {
  practice_name: string;
  specialty: string;
  emr_name: string;
  admin_name: string;
  admin_email: string;
  admin_phone: string;
  npi_number: string;
  npi_data: Record<string, unknown> | null;
  otp: string;
}

const SPECIALTIES = [
  'Ophthalmology', 'Orthopedics', 'Cardiology', 'Dermatology',
  'Neurology', 'Oncology', 'Gastroenterology', 'Urology',
  'Endocrinology', 'Pulmonology', 'Rheumatology', 'Other',
];

const EMRS = [
  'MaximEyes', 'Athena', 'Epic', 'Kareo', 'DrChrono',
  'Practice Fusion', 'eClinicalWorks', 'NextGen', 'Allscripts', 'Other',
];

export default function RegisterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState<FormData>({
    practice_name: '', specialty: '', emr_name: '', admin_name: '',
    admin_email: '', admin_phone: '', npi_number: '', npi_data: null, otp: '',
  });

  const set = (key: keyof FormData, value: unknown) => {
    setForm(f => ({ ...f, [key]: value }));
    setError('');
  };

  const handleStep1 = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/api/register/check-email`, { email: form.admin_email });
      setStep(2);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Email check failed');
    } finally { setLoading(false); }
  };

  const handleStep2 = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/register/verify-npi`, { npi: form.npi_number });
      set('npi_data', res.data.practice_info);
      if (res.data.practice_info.organization_name && !form.practice_name) {
        set('practice_name', res.data.practice_info.organization_name);
      }
      setStep(3);
    } catch (e: any) {
      setError(e.response?.data?.message || 'NPI verification failed');
    } finally { setLoading(false); }
  };

  const handleSendOtp = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/api/register/send-otp`, { mobile: form.admin_phone, email: form.admin_email });
      setStep(4);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to send OTP');
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/api/register/verify-otp`, { email: form.admin_email, otp: form.otp });
      setStep(5);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Invalid OTP');
    } finally { setLoading(false); }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/api/register/complete`, {
        practice_name: form.practice_name,
        specialty: form.specialty,
        emr_name: form.emr_name,
        admin_name: form.admin_name,
        admin_email: form.admin_email,
        admin_phone: form.admin_phone,
        npi_number: form.npi_number,
        npi_data: form.npi_data,
      });
      navigate('/login?registered=true');
    } catch (e: any) {
      setError(e.response?.data?.message || 'Registration failed');
    } finally { setLoading(false); }
  };

  const STEPS = [
    { num: 1, label: 'Email' },
    { num: 2, label: 'NPI' },
    { num: 3, label: 'Details' },
    { num: 4, label: 'Verify' },
    { num: 5, label: 'Review' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2A2A2A] to-[#161616] flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-white">RCMTask</h1>
          <p className="text-white/70 text-sm mt-1">Start your free 30-day trial</p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.num}>
              <div className={`flex flex-col items-center ${step >= s.num ? 'opacity-100' : 'opacity-40'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                  step > s.num ? 'bg-[#C94B1F] text-white' :
                  step === s.num ? 'bg-white text-[#C94B1F]' : 'bg-white/20 text-white'
                }`}>
                  {step > s.num ? <CheckCircle className="w-4 h-4" /> : s.num}
                </div>
                <span className="text-white/70 text-xs mt-1">{s.label}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`w-8 h-0.5 mb-4 ${step > s.num ? 'bg-[#C94B1F]' : 'bg-white/20'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Step 1: Email */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Practice Email</h2>
                <p className="text-sm text-gray-500 mt-1">Use your corporate or practice email address</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Admin Name *</label>
                <input type="text" value={form.admin_name}
                  onChange={e => set('admin_name', e.target.value)}
                  placeholder="Dr. Jane Smith"
                  className="w-full text-sm border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C94B1F]/20 focus:border-[#C94B1F]" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Corporate Email *</label>
                <input type="email" value={form.admin_email}
                  onChange={e => set('admin_email', e.target.value)}
                  placeholder="doctor@yourclinic.com"
                  className="w-full text-sm border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C94B1F]/20 focus:border-[#C94B1F]" />
                <p className="text-xs text-gray-400 mt-1">Gmail, Yahoo, Hotmail etc. are not accepted</p>
              </div>
              <button onClick={handleStep1} disabled={loading || !form.admin_email || !form.admin_name}
                className="w-full py-3 bg-[#C94B1F] text-white font-medium rounded-xl hover:bg-[#A8390F] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {loading ? <Loader className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          )}

          {/* Step 2: NPI */}
          {step === 2 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">NPI Verification</h2>
                <p className="text-sm text-gray-500 mt-1">We verify your NPI to confirm you are a licensed medical practice</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">NPI Number *</label>
                <input type="text" value={form.npi_number}
                  onChange={e => set('npi_number', e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="10-digit NPI number"
                  maxLength={10}
                  className="w-full text-sm border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C94B1F]/20 focus:border-[#C94B1F] font-mono tracking-widest" />
                <p className="text-xs text-gray-400 mt-1">
                  Look up your NPI at <a href="https://npiregistry.cms.hhs.gov" target="_blank" rel="noopener noreferrer" className="text-[#C94B1F] underline">npiregistry.cms.hhs.gov</a>
                </p>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setStep(1)}
                  className="px-4 py-3 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button onClick={handleStep2} disabled={loading || form.npi_number.length !== 10}
                  className="flex-1 py-3 bg-[#C94B1F] text-white font-medium rounded-xl hover:bg-[#A8390F] disabled:opacity-50 flex items-center justify-center gap-2">
                  {loading ? <Loader className="w-4 h-4 animate-spin" /> : <>Verify NPI <ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Practice Details */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Practice Details</h2>
                {form.npi_data && (
                  <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 mt-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <p className="text-xs text-green-700">NPI verified — {(form.npi_data as any).organization_name}</p>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Practice Name *</label>
                <input type="text" value={form.practice_name}
                  onChange={e => set('practice_name', e.target.value)}
                  className="w-full text-sm border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C94B1F]/20 focus:border-[#C94B1F]" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Specialty *</label>
                <select value={form.specialty} onChange={e => set('specialty', e.target.value)}
                  className="w-full text-sm border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C94B1F]/20 focus:border-[#C94B1F]">
                  <option value="">Select your specialty...</option>
                  {SPECIALTIES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">EMR System</label>
                <select value={form.emr_name} onChange={e => set('emr_name', e.target.value)}
                  className="w-full text-sm border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C94B1F]/20 focus:border-[#C94B1F]">
                  <option value="">Select your EMR...</option>
                  {EMRS.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setStep(2)}
                  className="px-4 py-3 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button onClick={handleSendOtp} disabled={loading || !form.practice_name || !form.specialty}
                  className="flex-1 py-3 bg-[#C94B1F] text-white font-medium rounded-xl hover:bg-[#A8390F] disabled:opacity-50 flex items-center justify-center gap-2">
                  {loading ? <Loader className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Email OTP (Phase A2 #9 — was SMS) */}
          {step === 4 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Verify Email</h2>
                <p className="text-sm text-gray-500 mt-1">We emailed a 6-digit code to {form.admin_email}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Mobile Number <span className="text-gray-400 font-normal">(optional)</span></label>
                <input type="tel" value={form.admin_phone}
                  onChange={e => set('admin_phone', e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  className="w-full text-sm border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C94B1F]/20 focus:border-[#C94B1F]" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Verification Code *</label>
                <input type="text" value={form.otp}
                  onChange={e => set('otp', e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000" maxLength={6}
                  className="w-full text-sm border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C94B1F]/20 focus:border-[#C94B1F] font-mono text-center text-xl tracking-widest" />
              </div>
              <button onClick={() => handleSendOtp()} className="text-xs text-[#C94B1F] hover:underline">
                Resend code
              </button>
              <div className="flex gap-3">
                <button onClick={() => setStep(3)}
                  className="px-4 py-3 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button onClick={handleVerifyOtp} disabled={loading || form.otp.length !== 6}
                  className="flex-1 py-3 bg-[#C94B1F] text-white font-medium rounded-xl hover:bg-[#A8390F] disabled:opacity-50 flex items-center justify-center gap-2">
                  {loading ? <Loader className="w-4 h-4 animate-spin" /> : <>Verify <ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Review & Complete */}
          {step === 5 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">All Verified ✅</h2>
                <p className="text-sm text-gray-500 mt-1">Review your details and start your free trial</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                {[
                  { label: 'Practice', value: form.practice_name },
                  { label: 'Specialty', value: form.specialty },
                  { label: 'Admin', value: form.admin_name },
                  { label: 'Email', value: form.admin_email },
                  { label: 'Mobile', value: form.admin_phone },
                  { label: 'NPI', value: form.npi_number },
                  { label: 'EMR', value: form.emr_name || 'Not specified' },
                ].map(item => (
                  <div key={item.label} className="flex justify-between">
                    <span className="text-gray-500">{item.label}</span>
                    <span className="font-medium text-gray-900">{item.value}</span>
                  </div>
                ))}
              </div>

              <div className="bg-[#FBEDE8] rounded-xl p-4">
                <p className="text-sm font-semibold text-[#C94B1F] mb-1">What happens next:</p>
                <ul className="text-xs text-[#C94B1F] space-y-1">
                  <li>✅ Account created instantly</li>
                  <li>✅ Sample data loaded so you can explore</li>
                  <li>✅ 30-day free trial begins now</li>
                  <li>✅ No credit card required yet</li>
                </ul>
              </div>

              <button onClick={handleComplete} disabled={loading}
                className="w-full py-3.5 bg-[#C94B1F] text-white font-semibold rounded-xl hover:bg-[#A8390F] transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-base">
                {loading ? <Loader className="w-5 h-5 animate-spin" /> : <>Start Free Trial — 30 Days Free</>}
              </button>

              <p className="text-xs text-gray-400 text-center">
                By creating an account you agree to our Terms of Service and HIPAA Business Associate Agreement
              </p>
            </div>
          )}
        </div>

        <p className="text-center text-white/60 text-sm mt-4">
          Already have an account?{' '}
          <a href="/login" className="text-white underline">Sign in</a>
        </p>
      </div>
    </div>
  );
}
